import type {
  CurrencyFormatOptions,
  CurrencySeparator,
} from 'components/datatable/datatable.types';
import { ZERO_COST } from 'constants/currency';
import type { Currency } from 'types/account';
import type { CostWithCurrency } from 'types/units';
import { getLocaleFromBrowser } from 'utils/browser-detection';
import { roundedValueToDecimalPlace } from './numbers';

export const formatCurrency = (currency: Currency, value?: number | null, digits = 0) => {
  const locale = getLocaleFromBrowser();
  const isWholeNumber = Number.isInteger(value);
  return (value ?? 0).toLocaleString(locale, {
    style: 'currency',
    currencyDisplay: 'symbol',
    currency: currency.iso_code,
    minimumFractionDigits: isWholeNumber ? digits : 2,
    maximumFractionDigits: 2,
  });
};

export function formatCost(amount: number = 0, currency?: Currency): CostWithCurrency {
  if (!amount && !currency) {
    return ZERO_COST;
  }

  const formatted = currency ? formatCurrency(currency, amount, 2) : undefined;
  const amountWithDecimals = amount.toFixed(2);
  return {
    formatted: formatted ?? amountWithDecimals,
    amount: parseFloat(amountWithDecimals),
    fractional: Math.floor(amount * 100),
    currency_code: currency?.id ?? '',
    currency,
  };
}

const currencySeparatorMap: Record<CurrencySeparator, string> = {
  comma: ',',
  period: '.',
  space: ' ',
  apostrophe: `'`,
} as const;

export const customFormatCurrency = (
  value: number,
  currency: Currency | null = null,
  { separator, symbolPlacement }: CurrencyFormatOptions = {
    separator: 'comma-period',
    symbolPlacement: 'before',
  }
) => {
  const [thousands, decimal] = separator.split('-');
  const thousandSeparator = currencySeparatorMap[thousands as CurrencySeparator];
  const decimalSeparator = currencySeparatorMap[decimal as CurrencySeparator];

  // Round to 2 decimal places
  value = roundedValueToDecimalPlace(value, 2);

  const parts = String(value).split('.');

  // Apply thousand separator
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);

  // Ensure there are 2 decimal places
  parts[1] = parts[1]?.padEnd(2, '0') ?? '00';

  // Apply decimal separator
  let formattedValue = parts.join(decimalSeparator);

  // Symbol placement
  if (currency && symbolPlacement !== 'hidden') {
    formattedValue =
      symbolPlacement === 'before'
        ? `${currency.symbol}${formattedValue}`
        : `${formattedValue}${currency.symbol}`;
  }

  return formattedValue;
};
